import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { FadeIn } from '../components/ui/FadeIn';
import { AppStoreButtons } from '../components/AppStoreButtons';
import { AudienceShowcase } from '../components/AudienceShowcase';
import { SiteFooter } from '../components/ui/SiteFooter';
import { SiteHeader } from '../components/ui/SiteHeader';
import { FoundingMemberWelcome } from '../components/practitioners/FoundingMemberWelcome';
import { appTourSteps, practitionerApplicationUrl } from '../data/appTour';
import { usePostCheckoutArrival } from '../hooks/usePostCheckoutArrival';
import { useScreenInit } from '../useScreenInit.js';
export function AppLaunchPage() {
  // Design-canvas preview only (Screens iframe); ignored on normal visits.
  const screen = useScreenInit() as {
    postCheckout?: boolean;
  };
  const preview = window.self !== window.top ? screen.postCheckout : undefined;
  const justCheckedOut = usePostCheckoutArrival(preview);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-660" data-mp="2n2dujwl3zx98">
      {/* Navigation */}
      <SiteHeader currentPath="/get-the-app" data-id="element-661" data-mp="3kpi4n6y83mcc" />

      <main data-id="element-662" data-mp="3kqs3vzgtb0kk">
        {justCheckedOut ? <FoundingMemberWelcome data-id="element-663" data-mp="3fz1mxanddek6" /> : (/* Launch hero */
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20" data-id="element-664" data-mp="3fwxb86j8bb9c">
          <div className="max-w-4xl" data-id="element-665" data-mp="3bvlukqic20da">
            <FadeIn data-id="element-666" data-mp="36z4xv7x2idol">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-6" data-id="element-667" data-mp="36zn3gv5me496">
                Now available on iOS and Android
              </p>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-charcoal leading-[1.05] tracking-tight" data-id="element-668" data-mp="2wzw4ag64712r">
                Your whole wellness team,{' '}
                <span className="italic text-purple-800" data-id="element-669" data-mp="2t16v6zs405ox">in your pocket</span>
              </h1>
              <p className="mt-7 max-w-2xl text-lg md:text-xl text-charcoal/70 font-light leading-relaxed" data-id="element-670" data-mp="v6ygiyr72yoo">
                Get matched with verified practitioners, book care that fits
                your life, and track how you're really doing — all in the Fem
                Entity app.
              </p>
            </FadeIn>

            <FadeIn delay={150} data-id="element-671" data-mp="2bprrvr9sjfi1">
              <AppStoreButtons className="mt-10" data-id="element-672" data-mp="2bqt5o3i6yg3g" />
              <p className="mt-5 text-sm text-charcoal/50" data-id="element-673" data-mp="2fr3ctjhpzcjw">
                Free to download. Available across Canada, excluding Quebec.
              </p>
            </FadeIn>
          </div>
        </section>)}

        {/* Two sides of the app */}
        <section aria-labelledby="audiences-heading" className="bg-purple-50 border-y border-purple-100 py-16 md:py-24" data-id="element-674" data-mp="38f8xy7cor3k9">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-675" data-mp="2zeubh4nzjif6">
            <FadeIn className="max-w-2xl mb-12" data-id="element-676" data-mp="2zd9hbg5l21hu">
              <h2 id="audiences-heading" className="font-serif text-3xl md:text-5xl text-charcoal leading-tight" data-id="element-677" data-mp="1j0b5pbcrkuu9">
                One app, both sides of care
              </h2>
              <p className="mt-5 text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-678" data-mp="1fhlrwh96nrp8">
                Built for the women seeking support, and for the practitioners
                who provide it.
              </p>
            </FadeIn>

            <AudienceShowcase data-id="element-679" data-mp="1peqhkr28m5as" />
          </div>
        </section>

        {/* The journey, in order */}
        <section aria-labelledby="journey-heading" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-id="element-680" data-mp="1kg6vmqnn8tif">
          <FadeIn data-id="element-681" data-mp="ru3ykha9slk5">
            <h2 id="journey-heading" className="font-serif text-3xl md:text-5xl text-charcoal leading-tight max-w-2xl" data-id="element-682" data-mp="runclmwsupsw">
              How it works, step by step
            </h2>
          </FadeIn>

          <ol className="mt-14 max-w-3xl border-l border-purple-200" data-id="element-683" data-mp="11pp3an6q3zxl">
            {appTourSteps.map((step, index) => <li key={step.title} className="relative pl-8 md:pl-12 pb-12 last:pb-0" data-id="element-684" data-mp="934g7yxa69zu" data-mpi={index}>
                <span aria-hidden="true" className="absolute -left-[13px] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-purple-700 text-[11px] font-semibold text-white" data-id="element-685" data-mp="38gtynmmb4yww">
                  {index + 1}
                </span>
                <h3 className="font-serif text-xl md:text-2xl text-charcoal leading-snug" data-id="element-686" data-mp="3m88ke17lne7n">
                  {step.title}
                </h3>
                <p className="mt-3 text-base text-charcoal/70 leading-[1.8]" data-id="element-687" data-mp="3r6a5ssshzpma">
                  {step.description}
                </p>
              </li>)}
          </ol>
        </section>

        {/* Practitioner path */}
        <section className="bg-purple-100/70 border-y border-purple-200" data-id="element-688" data-mp="4y0rbrk4h6b6">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-689" data-mp="90fciib9ki5m">
            <div className="max-w-2xl" data-id="element-690" data-mp="2at3gwuzn9bmz">
              <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-snug" data-id="element-691" data-mp="2au4tj3wb666t">
                Are you a practitioner?
              </h2>
              <p className="mt-3 text-base text-charcoal/70 leading-relaxed" data-id="element-692" data-mp="23bf2utla0zwu">
                Apply to join our vetted network and manage your schedule,
                services, and payments from the same app.
              </p>
            </div>
            <a href={practitionerApplicationUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-charcoal/25 px-7 py-4 text-base font-semibold text-charcoal transition-colors duration-200 ease-out hover:border-purple-700 hover:text-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-purple-100" data-id="element-693" data-mp="1piyx3l0zqns8">
              Apply to the network
              <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-694" data-mp="11q8mpg8qkck2" />
            </a>
          </div>
        </section>

        {/* Final download */}
        <section className="bg-charcoal py-20 md:py-28 text-center" data-id="element-695" data-mp="ybp8f9n2doev">
          <div className="max-w-3xl mx-auto px-6" data-id="element-696" data-mp="139if03sa4kfo">
            <FadeIn data-id="element-697" data-mp="138z5nbgp34d9">
              <h2 className="font-serif text-3xl md:text-5xl text-ivory leading-tight" data-id="element-698" data-mp="137e8rf6wr9c8">
                Start where you are
              </h2>
              <p className="mt-6 text-lg text-ivory/70 font-light leading-relaxed" data-id="element-699" data-mp="tat1en7f1m58">
                Download the app and take the matching quiz — your first
                practitioner match takes about two minutes.
              </p>
            </FadeIn>
            <AppStoreButtons tone="dark" className="mt-10 justify-center items-center" data-id="element-700" data-mp="kcpk633grbj8" />
          </div>
        </section>
      </main>

      <SiteFooter currentPath="/get-the-app" data-id="element-701" data-mp="almkn5q6n4m2" />
    </div>;
}